import { PageViewer } from "@/components/PageEditor";
import configuration from "@/configuration";
import { useWindowSize } from "@/hooks";
import { PageType } from "@/services/shared-models";
import { ContentService } from "@/services/user-services";
import { Page } from "@/services/user-services/models";
import { useDataStore, useLocaleStore } from "@/stores";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Element from "../element/Element";
import Elements from "../elements";

interface Content {
    page?: Page | null;
    subPageSlug?: string;
    isLoading: boolean;
}

const ContentPage = () => {
    const activeRegion = useLocaleStore((state) => state.activeRegion);
    const activeLanguage = useLocaleStore((state) => state.activeLanguage);
    const pages = useDataStore((state) => state.pages);
    const setComments = useDataStore((state) => state.setComments);

    const [content, setContent] = useState<Content>({ isLoading: true });

    const pageService = useMemo(
        () => new ContentService(activeRegion!, activeLanguage!),
        [activeRegion, activeLanguage]
    );

    const { "*": pageSlug } = useParams();

    useEffect(() => {
        setContent((state) => ({ ...state, isLoading: true }));

        if (!pageSlug) {
            const homePage = pages.find((x) => x.pageType === PageType.Home);
            if (homePage) {
                pageService.getPage(homePage.slug).then((page) => {
                    setContent({ page, subPageSlug: "", isLoading: false });
                });

                return;
            }
        } else {
            const page = pages.find((x) => x.url === pageSlug);
            if (page) {
                const pageType = PageType[page.pageType as keyof typeof PageType];
                switch (pageType) {
                    case PageType.Home:
                    case PageType.Step1:
                    case PageType.Step2:
                    case PageType.Step3:
                    case PageType.Content:
                    case PageType.Download:
                    case PageType.Elements:
                        pageService.getPage(page.slug).then((page) => {
                            setContent({ page, subPageSlug: "", isLoading: false });
                            if (!page) return;
                            setComments(page.slug, page.comments || []);
                        });
                        break;

                    case PageType.Master:
                        {
                            const children = pages
                                .filter((x) => x.parentPageId === page.id)
                                .sort((a, b) => a.parentOrder - b.parentOrder);

                            if (children) {
                                pageService.getPage(children[0].slug).then((page) => {
                                    setContent({ page, subPageSlug: "", isLoading: false });
                                    if (!page) return;
                                    setComments(page.slug, page.comments || []);
                                });
                            }
                        }
                        break;
                }

                return;
            }

            const partialElementPageMatch = pages.find(
                (x) => x.pageType === PageType.Elements && pageSlug?.startsWith(`${x.url}/`)
            );

            if (partialElementPageMatch) {
                setContent({ page: partialElementPageMatch, subPageSlug: pageSlug, isLoading: false });
                return;
            }
        }

        setContent({ isLoading: false, page: undefined });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pages, pageSlug]);

    const renderContent = ({ page, subPageSlug }: Content) => {
        if (page!.pageType === PageType.Elements) {
            if (subPageSlug) return <Element />;
            return <Elements />;
        }

        return <PageViewer data={page!.content} slug={page!.slug} />;
    };

    const renderPage = () => {
        const activePage = content.page;
        if (activePage) {
            return (
                <>
                    <Helmet>
                        <title>BIMids - {activePage.title}</title>

                        <link rel="canonical" href={`${configuration.canonical}${window.location.pathname}`} />

                        <meta name="robots" content="index, follow" />

                        <meta name="title" content={`BIMids - ${activePage.title}`} />
                        <meta name="description" content={activePage.description} />

                        <meta property="og:title" content={`BIMids - ${activePage.title}`} />
                        <meta property="og:url" content={window.location.href} />
                        <meta property="og:description" content={activePage.description} />

                        <meta property="twitter:title" content={`BIMids - ${activePage.title}`} />
                        <meta property="twitter:url" content={window.location.href} />
                        <meta property="twitter:description" content={activePage.description} />
                    </Helmet>
                    {renderContent(content)}{" "}
                </>
            );
        }

        return <NotFound />;
    };

    if (content.isLoading && !content.page) return <div className="h-screen" />;

    return renderPage();
};

export default ContentPage;

const NotFound = () => {
    const [width] = useWindowSize();
    const [left, setLeft] = useState(0);

    const { t } = useTranslation("common");

    useLayoutEffect(() => {
        if (width === 2560) {
            setLeft(0);
        } else {
            const change = 2560 - width;
            setLeft(change * -0.5);
        }
    }, [width]);

    return (
        <>
            <Helmet>
                <title>BIMids - Error 404</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <div
                className={`absolute w-full h-full bg-primary-100 overflow-hidden`}
                style={{
                    left: `${left}px`,
                    width: 2015,
                    clipPath: "polygon(200px 0, 2015px 0, 838px 450px)",
                }}
            />

            <div className="container mx-auto 2xl:px-[108px]">
                <section id="hero" className="relative pt-[60px] mb-1">
                    <div className="flex flex-col mb-100">
                        <h1 className="flex content-center gap-3 items-center">
                            {t("not-found.title", { ns: "common" })}
                        </h1>
                        <div className="text-lg/[32px] font-medium mt-2 max-w-[920px]">
                            {t("not-found.description", { ns: "common" })}
                        </div>
                    </div>
                </section>

                <hr className="h-px bg-secondary-300 border-0" />
            </div>
        </>
    );
};
